/**
 * NutriScoreSection — Nutri-Score badge, settings card, and sandbox calculator.
 * Used inside RecipeDetail.
 */
import React, { useState, useCallback } from 'react';
import { Button, Card, Form, Col, Row, Table, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    calculateNutriScore,
    sandboxNutriScore,
    updateNutriScoreLabel,
} from '../../../services/RecipesService';
import { kcalToKj } from '../../../utils/nutritionUtils';

// ── Colour constants ─────────────────────────────────────────────────────────

export const NS_COLORS = {
    A: '#038141',
    B: '#85BB2F',
    C: '#FECB02',
    D: '#EE8100',
    E: '#E63312',
};
export const NS_TEXT_COLORS = {
    A: '#fff', B: '#fff', C: '#000', D: '#fff', E: '#fff',
};
export const LETTERS = ['A', 'B', 'C', 'D', 'E'];

// ── Nutri-Score badge (5-block strip) ────────────────────────────────────────

export function NutriScoreBadge({ letter, score, compact = false }) {
    if (!letter || !LETTERS.includes(letter)) return null;

    if (compact) {
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: NS_COLORS[letter],
                    color: NS_TEXT_COLORS[letter],
                    borderRadius: 4,
                    fontWeight: 700,
                    fontSize: 13,
                    width: 26,
                    height: 26,
                    lineHeight: 1,
                }}
                title={`Nutri-Score ${letter}`}
            >
                {letter}
            </span>
        );
    }

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                {LETTERS.map((l) => {
                    const active = l === letter;
                    return (
                        <div
                            key={l}
                            style={{
                                background: NS_COLORS[l],
                                color: NS_TEXT_COLORS[l],
                                borderRadius: active ? 5 : 3,
                                width: active ? 38 : 28,
                                height: active ? 42 : 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: active ? 20 : 13,
                                boxShadow: active ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
                                border: active ? '2px solid rgba(0,0,0,0.18)' : 'none',
                                transition: 'all 0.1s',
                            }}
                        >
                            {l}
                        </div>
                    );
                })}
            </div>
            <div style={{ fontSize: 10, color: '#666', textAlign: 'center' }}>
                Nutri-Score
                {score != null && (
                    <span style={{ marginLeft: 4, color: '#999' }}>({score})</span>
                )}
            </div>
        </div>
    );
}

// ── Category options ─────────────────────────────────────────────────────────

function useCategoryOptions(t) {
    return [
        { value: 'general', label: t('nutriscore.categories.general') },
        { value: 'red_meat', label: t('nutriscore.categories.red_meat') },
        { value: 'cheese', label: t('nutriscore.categories.cheese') },
        { value: 'fats', label: t('nutriscore.categories.fats') },
        { value: 'beverages', label: t('nutriscore.categories.beverages') },
    ];
}

// ── Score breakdown table ────────────────────────────────────────────────────

function BreakdownTable({ result, t }) {
    const { points_a, points_c, score, breakdown } = result;
    return (
        <Table size="sm" bordered className="mb-0" style={{ fontSize: 13 }}>
            <tbody>
                <tr style={{ background: '#fff5f5' }}>
                    <td colSpan={2} className="fw-semibold text-danger">
                        {t('nutriscore.breakdown.points_a')}
                    </td>
                    <td className="text-end fw-bold text-danger">{points_a}</td>
                </tr>
                {[
                    ['energy', t('nutriscore.breakdown.energy')],
                    ['sugars', t('nutriscore.breakdown.sugars')],
                    ['sfa', t('nutriscore.breakdown.sfa')],
                    ['salt', t('nutriscore.breakdown.salt')],
                ].map(([key, label]) => (
                    <tr key={key}>
                        <td style={{ paddingLeft: 20 }}>{label}</td>
                        <td></td>
                        <td className="text-end">{breakdown?.[key] ?? '—'}</td>
                    </tr>
                ))}

                <tr style={{ background: '#f0fff4' }}>
                    <td colSpan={2} className="fw-semibold text-success">
                        {t('nutriscore.breakdown.points_c')}
                    </td>
                    <td className="text-end fw-bold text-success">{points_c}</td>
                </tr>
                {[
                    ['fvl', t('nutriscore.breakdown.fvl')],
                    ['fibre', t('nutriscore.breakdown.fibre')],
                    ['protein', t('nutriscore.breakdown.protein')],
                ].map(([key, label]) => (
                    <tr key={key}>
                        <td style={{ paddingLeft: 20 }}>{label}</td>
                        <td></td>
                        <td className="text-end">{breakdown?.[key] ?? '—'}</td>
                    </tr>
                ))}

                <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
                    <td colSpan={2}>{t('nutriscore.breakdown.final')}</td>
                    <td className="text-end">{score}</td>
                </tr>
            </tbody>
        </Table>
    );
}

// ── Shared form fields ───────────────────────────────────────────────────────

function NutriScoreForm({ values, onChange, isBeverages, t, categoryOptions, readOnlyFvl = false }) {
    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
                    {t('nutriscore.category')}
                </Form.Label>
                <Form.Select
                    size="sm"
                    value={values.category}
                    onChange={(e) => onChange('category', e.target.value)}
                >
                    {categoryOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
                    {t('nutriscore.fvl_percent')}
                </Form.Label>
                {readOnlyFvl ? (
                    <div
                        className="form-control form-control-sm"
                        style={{ background: '#f8f9fa', color: '#6c757d', cursor: 'default' }}
                    >
                        {typeof values.fvl_percent === 'number'
                            ? `${values.fvl_percent.toFixed(1)} %`
                            : `${parseFloat(values.fvl_percent || 0).toFixed(1)} %`}
                    </div>
                ) : (
                    <Form.Control
                        type="number"
                        size="sm"
                        min={0}
                        max={100}
                        step="any"
                        value={values.fvl_percent}
                        onChange={(e) => onChange('fvl_percent', e.target.value)}
                    />
                )}
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                    {readOnlyFvl ? t('nutriscore.fvl_auto') : t('nutriscore.fvl_help')}
                </Form.Text>
            </Form.Group>

            {isBeverages && (
                <>
                    <Form.Check
                        type="switch"
                        id="ns-is-water"
                        className="mb-2"
                        label={<span style={{ fontSize: 13 }}>{t('nutriscore.is_water')}</span>}
                        checked={!!values.is_water}
                        onChange={(e) => onChange('is_water', e.target.checked)}
                    />
                    <Form.Check
                        type="switch"
                        id="ns-has-sweeteners"
                        className="mb-3"
                        label={<span style={{ fontSize: 13 }}>{t('nutriscore.has_sweeteners')}</span>}
                        checked={!!values.has_sweeteners}
                        onChange={(e) => onChange('has_sweeteners', e.target.checked)}
                    />
                </>
            )}
        </>
    );
}

// ── Main NutriScoreSection ───────────────────────────────────────────────────

export default function NutriScoreSection({ recipe, nutrition, portionWeightG, onRecipeUpdated }) {
    const { t } = useTranslation();
    const categoryOptions = useCategoryOptions(t);

    // Settings form state (for calculate-and-save)
    const [formValues, setFormValues] = useState({
        category: recipe?.nutriscore_category || 'general',
        fvl_percent: recipe?.nutriscore_fvl_percent ?? 0,
        is_water: recipe?.nutriscore_is_water ?? false,
        has_sweeteners: recipe?.nutriscore_has_sweeteners ?? false,
    });

    // Current saved result (mirrors recipe fields)
    const [savedResult, setSavedResult] = useState(
        recipe?.nutriscore_letter
            ? {
                  letter: recipe.nutriscore_letter,
                  score: recipe.nutriscore_score,
                  // breakdown not available without re-calc; shown after calc
              }
            : null
    );
    const [savedBreakdown, setSavedBreakdown] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);

    const [saving, setSaving] = useState(false);
    const [showOnLabel, setShowOnLabel] = useState(!!recipe?.nutriscore_show_on_label);

    // Sandbox state
    const [sandboxValues, setSandboxValues] = useState({
        category: 'general',
        energy_kj: '',
        sugars_g: '',
        saturated_fat_g: '',
        salt_g: '',
        fvl_percent: 0,
        fiber_g: '',
        protein_g: '',
        is_water: false,
        has_sweeteners: false,
    });
    const [sandboxResult, setSandboxResult] = useState(null);
    const [sandboxCalcing, setSandboxCalcing] = useState(false);
    const [showSandbox, setShowSandbox] = useState(false);

    const handleFormChange = useCallback((key, val) => {
        setFormValues((prev) => ({ ...prev, [key]: val }));
    }, []);

    const handleSandboxChange = useCallback((key, val) => {
        setSandboxValues((prev) => ({ ...prev, [key]: val }));
    }, []);

    // Load current recipe per-100g values into sandbox
    const loadSandboxFromRecipe = useCallback(() => {
        if (!nutrition) {
            toast.warning(t('nutriscore.insufficient_data'));
            return;
        }
        const perPortion = nutrition.perServing ?? nutrition.total;
        if (!perPortion) return;

        const pw = parseFloat(portionWeightG);
        const factor = pw > 0 ? 100 / pw : 1;
        const p100 = (val) => (val != null ? +(val * factor).toFixed(2) : '');

        setSandboxValues((prev) => ({
            ...prev,
            energy_kj: +(kcalToKj(perPortion.energy) * factor).toFixed(2) || '',
            sugars_g: p100(perPortion.sugars),
            saturated_fat_g: p100(perPortion.saturatedFat),
            // sodium stored in g; convert to salt: g * 2.54
            salt_g: perPortion.sodium != null ? +((perPortion.sodium * factor) * 2.54).toFixed(4) : '',
            fiber_g: p100(perPortion.fiber),
            protein_g: p100(perPortion.protein),
        }));
        toast.info(t('nutriscore.sandbox.load'));
    }, [nutrition, portionWeightG, t]);

    // Calculate and save
    const handleCalculate = async () => {
        setSaving(true);
        try {
            const res = await calculateNutriScore(recipe.id, {
                category: formValues.category,
                is_water: !!formValues.is_water,
                has_sweeteners: !!formValues.has_sweeteners,
            });
            const d = res.data;
            setSavedResult(d);
            setSavedBreakdown(d.breakdown);
            setShowBreakdown(true);
            // Sync displayed FVL% to the auto-computed value from the backend
            if (d.fvl_percent != null) {
                setFormValues((prev) => ({ ...prev, fvl_percent: d.fvl_percent }));
            }
            if (onRecipeUpdated) onRecipeUpdated(d);
            toast.success(`Nutri-Score ${d.letter} (${d.score})`);
        } catch (err) {
            const msg = err?.response?.data?.error || t('nutriscore.errors.calculate');
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // Toggle show_on_label
    const handleLabelToggle = async (e) => {
        const val = e.target.checked;
        setShowOnLabel(val);
        try {
            await updateNutriScoreLabel(recipe.id, val);
            if (onRecipeUpdated) onRecipeUpdated({ nutriscore_show_on_label: val });
        } catch {
            toast.error(t('nutriscore.errors.label'));
            setShowOnLabel(!val);
        }
    };

    // Sandbox calculate
    const handleSandboxCalc = async () => {
        setSandboxCalcing(true);
        try {
            const res = await sandboxNutriScore({
                category: sandboxValues.category,
                energy_kj: parseFloat(sandboxValues.energy_kj) || 0,
                sugars_g: parseFloat(sandboxValues.sugars_g) || 0,
                saturated_fat_g: parseFloat(sandboxValues.saturated_fat_g) || 0,
                salt_g: parseFloat(sandboxValues.salt_g) || 0,
                fvl_percent: parseFloat(sandboxValues.fvl_percent) || 0,
                fiber_g: parseFloat(sandboxValues.fiber_g) || 0,
                protein_g: parseFloat(sandboxValues.protein_g) || 0,
                is_water: !!sandboxValues.is_water,
                has_sweeteners: !!sandboxValues.has_sweeteners,
            });
            setSandboxResult(res.data);
        } catch {
            toast.error(t('nutriscore.errors.calculate'));
        } finally {
            setSandboxCalcing(false);
        }
    };

    const isBeverages = formValues.category === 'beverages';
    const isSandboxBeverages = sandboxValues.category === 'beverages';

    return (
        <>
            {/* ── Nutri-Score Settings Card ── */}
            <Card className="mb-3" style={{ border: '1.5px solid #e8e0f7' }}>
                <Card.Header
                    className="d-flex justify-content-between align-items-center"
                    style={{ background: '#f8f5ff', borderBottom: '1px solid #e8e0f7' }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <span className="fw-semibold" style={{ color: '#452B90' }}>
                            {t('nutriscore.title')}
                        </span>
                        {savedResult?.letter && (
                            <NutriScoreBadge letter={savedResult.letter} score={savedResult.score} />
                        )}
                    </div>
                    {!savedResult?.letter && (
                        <small className="text-muted">{t('nutriscore.not_calculated')}</small>
                    )}
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <NutriScoreForm
                                values={formValues}
                                onChange={handleFormChange}
                                isBeverages={isBeverages}
                                t={t}
                                categoryOptions={categoryOptions}
                                readOnlyFvl
                            />
                        </Col>
                        <Col md={6} className="d-flex flex-column justify-content-end">
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleCalculate}
                                    disabled={saving}
                                    style={{ background: '#452B90', border: 'none' }}
                                >
                                    {saving ? t('nutriscore.calculating') : (
                                        savedResult?.letter
                                            ? t('nutriscore.recalculate')
                                            : t('nutriscore.calculate')
                                    )}
                                </Button>
                                {savedResult?.breakdown && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setShowBreakdown((p) => !p)}
                                    >
                                        {t('nutriscore.breakdown.title')}
                                    </Button>
                                )}
                            </div>

                            {savedResult?.letter && (
                                <Form.Check
                                    type="switch"
                                    id="ns-show-on-label"
                                    className="mb-2"
                                    label={<span style={{ fontSize: 13 }}>{t('nutriscore.show_on_label')}</span>}
                                    checked={showOnLabel}
                                    onChange={handleLabelToggle}
                                />
                            )}
                        </Col>
                    </Row>

                    {/* Breakdown collapsible */}
                    <Collapse in={showBreakdown && !!savedBreakdown}>
                        <div className="mt-3">
                            <div className="fw-semibold mb-2" style={{ fontSize: 13, color: '#452B90' }}>
                                {t('nutriscore.breakdown.title')}
                            </div>
                            {savedBreakdown && savedResult && (
                                <BreakdownTable result={{ ...savedResult, breakdown: savedBreakdown }} t={t} />
                            )}
                        </div>
                    </Collapse>
                </Card.Body>
            </Card>

            {/* ── Sandbox Card ── */}
            <Card className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                <Card.Header
                    style={{ cursor: 'pointer', userSelect: 'none', background: '#f8f9fa' }}
                    onClick={() => setShowSandbox((p) => !p)}
                    className="d-flex justify-content-between align-items-center"
                >
                    <div>
                        <span className="fw-semibold" style={{ fontSize: 14 }}>
                            {t('nutriscore.sandbox.title')}
                        </span>
                        <small className="text-muted ms-2">— {t('nutriscore.sandbox.subtitle')}</small>
                    </div>
                    <i className={`fa-solid fa-chevron-${showSandbox ? 'up' : 'down'} text-muted`} />
                </Card.Header>

                <Collapse in={showSandbox}>
                    <div>
                        <Card.Body>
                            <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                                <i className="fa-solid fa-circle-info me-1" />
                                {t('nutriscore.nothing_saved')}
                            </div>

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="mb-3"
                                onClick={loadSandboxFromRecipe}
                            >
                                <i className="fa-solid fa-arrow-down me-1" />
                                {t('nutriscore.sandbox.load')}
                            </Button>

                            <Row>
                                <Col md={6}>
                                    <NutriScoreForm
                                        values={sandboxValues}
                                        onChange={handleSandboxChange}
                                        isBeverages={isSandboxBeverages}
                                        t={t}
                                        categoryOptions={categoryOptions}
                                    />
                                </Col>
                                <Col md={6}>
                                    {/* Nutrition inputs */}
                                    {[
                                        { key: 'energy_kj', label: t('nutriscore.inputs.energy_kj') },
                                        { key: 'sugars_g', label: t('nutriscore.inputs.sugars_g') },
                                        { key: 'saturated_fat_g', label: t('nutriscore.inputs.saturated_fat_g') },
                                        { key: 'salt_g', label: t('nutriscore.inputs.salt_g') },
                                        { key: 'fiber_g', label: t('nutriscore.inputs.fiber_g') },
                                        { key: 'protein_g', label: t('nutriscore.inputs.protein_g') },
                                    ].map(({ key, label }) => (
                                        <Form.Group key={key} className="mb-2">
                                            <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>{label}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                min={0}
                                                step="any"
                                                value={sandboxValues[key]}
                                                onChange={(e) => handleSandboxChange(key, e.target.value)}
                                            />
                                        </Form.Group>
                                    ))}
                                </Col>
                            </Row>

                            <Button
                                variant="success"
                                size="sm"
                                className="mt-2"
                                onClick={handleSandboxCalc}
                                disabled={sandboxCalcing}
                            >
                                {sandboxCalcing ? t('nutriscore.calculating') : t('nutriscore.calculate_btn')}
                            </Button>

                            {sandboxResult && (
                                <div className="mt-3">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <NutriScoreBadge letter={sandboxResult.letter} score={sandboxResult.score} />
                                        <div style={{ fontSize: 13, color: '#666' }}>
                                            {t(`nutriscore.letters.${sandboxResult.letter}`)}
                                        </div>
                                    </div>
                                    <BreakdownTable result={sandboxResult} t={t} />
                                </div>
                            )}
                        </Card.Body>
                    </div>
                </Collapse>
            </Card>
        </>
    );
}
